import type { AwsRum, AwsRumConfig } from "aws-rum-web";
import type { CloudWatchRumErrorSource } from "@/lib/client/cloudwatch-rum-error-source";
import {
  buildSafeApiUrlPatterns,
  buildSensitiveApiUrlPatterns,
  getSafeRumPageTitle,
  isSafeApiUrl,
  toSafeRumApiFamily,
  toSafeRumPageId,
} from "@/lib/client/cloudwatch-rum-route";
import { APP_AWS_REGION } from "@/lib/config/platform";
import { isSafeObservabilityRelease } from "@/lib/shared/observability-release";

const JS_ERROR_EVENT_TYPE = "com.amazon.rum.js_error_event";
const SAFE_WEB_VITAL_EVENT_TYPES = {
  CLS: "com.amazon.rum.cumulative_layout_shift_event",
  INP: "com.amazon.rum.interaction_to_next_paint_event",
  LCP: "com.amazon.rum.largest_contentful_paint_event",
} as const;
const MAX_METADATA_VALUE_LENGTH = 256;
const MAX_QUEUED_ERRORS = 10;
const MAX_SAFE_STACK_FRAMES = 20;
const SLOW_API_RESPONSE_MS = 5_000;
const SAFE_CORRELATION_ID_PATTERN = /^[A-Za-z0-9_.:-]+$/;
const SAFE_STACK_LOCATION =
  /(?:https?:\/\/[A-Za-z0-9.:-]+)?(\/_next\/static\/[A-Za-z0-9_./-]+\.js):([1-9]\d{0,8}):([1-9]\d{0,8})/;
const SAFE_FUNCTION_NAME = /^[A-Za-z_$<][A-Za-z0-9_.$<>[\]-]{0,119}$/;
const SAFE_ERROR_NAMES = new Set([
  "AbortError",
  "AggregateError",
  "ApiClientError",
  "ChunkLoadError",
  "ConstraintError",
  "DataError",
  "EncodingError",
  "Error",
  "EvalError",
  "InvalidStateError",
  "NetworkError",
  "NotAllowedError",
  "NotFoundError",
  "NotReadableError",
  "OperationError",
  "QuotaExceededError",
  "RangeError",
  "ReferenceError",
  "SyntaxError",
  "TimeoutError",
  "TypeError",
  "URIError",
]);
const SUPPRESSED_ERROR_NAMES = new Set(["AbortError", "ChunkLoadError"]);

const PUBLIC_RUM_ENV = {
  applicationId: process.env["NEXT_PUBLIC_CLOUDWATCH_RUM_APPLICATION_ID"],
  identityPoolId: process.env["NEXT_PUBLIC_CLOUDWATCH_RUM_IDENTITY_POOL_ID"],
  releaseId: process.env["NEXT_PUBLIC_APP_RELEASE_ID"],
};

type RumClient = AwsRum;
type EventMetadata = Record<string, string | number | boolean>;

export type CloudWatchRumPublicEnvironment = {
  applicationId?: string | undefined;
  identityPoolId?: string | undefined;
  region?: string | undefined;
  releaseId?: string | undefined;
};

export type CloudWatchRumRuntimeConfig = {
  applicationId: string;
  identityPoolId: string;
  region: string;
  releaseId?: string;
};

export type CloudWatchRumActor = {
  userCorrelationId: string;
  organizationCorrelationId?: string;
  role: string;
  contractType?: string;
};

type PendingError = {
  error: Error;
  metadata: EventMetadata;
};

type SafeWebVitalMetric = {
  name: keyof typeof SAFE_WEB_VITAL_EVENT_TYPES;
  value: number;
};

let rumClient: RumClient | undefined;
let initialization: Promise<RumClient | undefined> | undefined;
let currentActor: CloudWatchRumActor | undefined;
let pendingErrorMetadata: EventMetadata | undefined;
const pendingErrors: PendingError[] = [];
const clientErrorIds = new WeakMap<object, string>();
const reportedClientErrorContexts = new WeakMap<object, Set<string>>();

function nonEmpty(value: string | undefined) {
  const normalized = value?.trim();
  return normalized ? normalized : undefined;
}

function safeMetadataValue(value: unknown) {
  if (typeof value !== "string") return undefined;
  const normalized = value.trim();
  if (!normalized || normalized.length > MAX_METADATA_VALUE_LENGTH) {
    return undefined;
  }
  return normalized;
}

function safeCorrelationId(value: unknown) {
  const normalized = safeMetadataValue(value);
  return normalized && SAFE_CORRELATION_ID_PATTERN.test(normalized)
    ? normalized
    : undefined;
}

function createClientErrorId() {
  try {
    const randomUuid = globalThis.crypto?.randomUUID?.();
    if (randomUuid) return `client_${randomUuid}`;
  } catch {
    // Correlation IDs must not affect application error handling.
  }
  return `client_${Date.now().toString(36)}_${Math.random()
    .toString(36)
    .slice(2, 10)}`;
}

function resolveClientErrorId(error: unknown, requestedErrorId: unknown) {
  const requested = safeCorrelationId(requestedErrorId);
  if (error === null || typeof error !== "object") {
    return requested ?? createClientErrorId();
  }

  if (requested) {
    clientErrorIds.set(error, requested);
    return requested;
  }
  const existing = clientErrorIds.get(error);
  if (existing) return existing;
  const resolved = createClientErrorId();
  clientErrorIds.set(error, resolved);
  return resolved;
}

export function getOrCreateCloudWatchRumErrorId(
  error: unknown,
  requestedErrorId?: string,
) {
  if (error !== null && typeof error === "object") {
    const existing = clientErrorIds.get(error);
    if (existing) return existing;
  }
  return resolveClientErrorId(error, requestedErrorId);
}

function safeObservabilityCorrelationId(
  kind: "user" | "organization",
  value: unknown,
) {
  const normalized = safeMetadataValue(value);
  return normalized &&
    new RegExp(`^obs_${kind}_[A-Za-z0-9_-]{22}$`).test(normalized)
    ? normalized
    : undefined;
}

export function resolveCloudWatchRumRuntimeConfig(
  environment: CloudWatchRumPublicEnvironment = PUBLIC_RUM_ENV,
): CloudWatchRumRuntimeConfig | undefined {
  const applicationId = nonEmpty(environment.applicationId);
  const identityPoolId = nonEmpty(environment.identityPoolId);
  if (!applicationId || !identityPoolId) return undefined;

  const releaseId = nonEmpty(environment.releaseId);
  return {
    applicationId,
    identityPoolId,
    region: APP_AWS_REGION,
    ...(isSafeObservabilityRelease(releaseId) ? { releaseId } : {}),
  };
}

function getRequestId(value: unknown, depth = 0): string | undefined {
  if (depth > 1 || value === null || typeof value !== "object") {
    return undefined;
  }

  const record = value as Record<string, unknown>;
  const requestId = safeCorrelationId(record["requestId"]);
  if (requestId) return requestId;
  return getRequestId(record["cause"], depth + 1);
}

function getSafeErrorName(error: unknown) {
  if (error === null || typeof error !== "object") return "Error";
  const name = (error as Record<string, unknown>)["name"];
  return typeof name === "string" && SAFE_ERROR_NAMES.has(name)
    ? name
    : "Error";
}

function getErrorStack(error: unknown) {
  if (error === null || typeof error !== "object") return undefined;
  const stack = (error as Record<string, unknown>)["stack"];
  return typeof stack === "string" ? stack : undefined;
}

function sanitizeStackFunctionName(prefix: string) {
  const normalized = prefix
    .replace(/^at\s+/, "")
    .replace(/@$/, "")
    .replace(/\($/, "")
    .replace(/^(?:async|new)\s+/, "")
    .trim();
  return SAFE_FUNCTION_NAME.test(normalized) ? normalized : "<anonymous>";
}

function sanitizeStackFrame(line: string) {
  const normalized = line.trim();
  if (
    (!normalized.startsWith("at ") && !normalized.includes("@http")) ||
    /[?#]/.test(normalized) ||
    /\beval\b/i.test(normalized)
  ) {
    return undefined;
  }

  const location = normalized.match(SAFE_STACK_LOCATION);
  if (!location || location.index === undefined) return undefined;
  const [matchedLocation, path, lineNumber, columnNumber] = location;
  if (!path || path.includes("..") || !lineNumber || !columnNumber) {
    return undefined;
  }

  const suffix = normalized.slice(location.index + matchedLocation.length);
  if (suffix !== "" && suffix !== ")") return undefined;
  const functionName = sanitizeStackFunctionName(
    normalized.slice(0, location.index),
  );
  return `at ${functionName} (${path}:${lineNumber}:${columnNumber})`;
}

export function sanitizeCloudWatchRumError(error: unknown) {
  const name = getSafeErrorName(error);
  const sanitized = new Error(name);
  sanitized.name = name;
  const stack = getErrorStack(error);
  const frames = stack
    ?.split("\n")
    .map(sanitizeStackFrame)
    .filter((frame): frame is string => Boolean(frame))
    .slice(0, MAX_SAFE_STACK_FRAMES);

  if (frames?.length) sanitized.stack = frames.join("\n");
  else delete sanitized.stack;
  return sanitized;
}

function recordSanitizedError(
  error: unknown,
  metadata: EventMetadata,
  client: RumClient | undefined = rumClient,
) {
  if (SUPPRESSED_ERROR_NAMES.has(getSafeErrorName(error))) return;
  const sanitized = sanitizeCloudWatchRumError(error);
  if (!client) {
    if (pendingErrors.length >= MAX_QUEUED_ERRORS) pendingErrors.shift();
    pendingErrors.push({ error: sanitized, metadata });
    return;
  }

  pendingErrorMetadata = metadata;
  try {
    client.recordError(sanitized);
  } catch {
    // Observability must never change application error handling.
  } finally {
    if (pendingErrorMetadata === metadata) pendingErrorMetadata = undefined;
  }
}

function captureGlobalErrorContext(event: ErrorEvent | PromiseRejectionEvent) {
  const isRejection = "reason" in event;
  const error = isRejection ? event.reason : event.error;
  const source = isRejection ? "unhandled_rejection" : "window_error";
  captureCloudWatchRumError(error, { handled: false, source });
  return true;
}

function getActorMetadata(): EventMetadata {
  if (!currentActor) return {};
  return {
    user_correlation_id: currentActor.userCorrelationId,
    ...(currentActor.organizationCorrelationId
      ? {
          organization_correlation_id: currentActor.organizationCorrelationId,
        }
      : {}),
    role: currentActor.role,
    ...(currentActor.contractType
      ? { contract_type: currentActor.contractType }
      : {}),
  };
}

function consumeErrorMetadata(eventType: string) {
  if (eventType !== JS_ERROR_EVENT_TYPE || !pendingErrorMetadata) return {};
  const metadata = pendingErrorMetadata;
  pendingErrorMetadata = undefined;
  return metadata;
}

function setRumMetadataHook(client: RumClient) {
  client.setEventMetadataHook((eventType) => ({
    ...getActorMetadata(),
    ...consumeErrorMetadata(eventType),
  }));
}

function startRumSession(client: RumClient) {
  // CloudWatch RUM UserDetails only accepts UUIDs generated by the SDK.
  // Stable application actor IDs remain searchable in event metadata.
  client.startSession();
}

export function recordSafeWebVital(
  client: Pick<RumClient, "recordEvent">,
  metric: SafeWebVitalMetric,
) {
  if (!Number.isFinite(metric.value) || metric.value < 0) return;
  try {
    client.recordEvent(SAFE_WEB_VITAL_EVENT_TYPES[metric.name], {
      version: "1.0.0",
      value: metric.value,
    });
  } catch {
    // Performance telemetry must never affect application behavior.
  }
}

async function installSafeWebVitalsCapture(client: RumClient) {
  // The attribution build used by the stock RUM performance telemetry adds
  // LCP resource URLs and DOM selectors. The base web-vitals callbacks retain
  // the diagnostic values without those customer-controlled strings.
  const { onCLS, onINP, onLCP } = await import("web-vitals");
  onCLS((metric) => recordSafeWebVital(client, metric));
  onINP((metric) => recordSafeWebVital(client, metric));
  onLCP((metric) => recordSafeWebVital(client, metric));
}

function recordCurrentPage(client: RumClient, pathname: string) {
  const pageId = toSafeRumPageId(pathname);
  client.recordPageView({
    pageId,
    pageAttributes: { title: getSafeRumPageTitle(pageId) },
  });
}

function recordPendingErrors(client: RumClient) {
  for (const pending of pendingErrors.splice(0)) {
    recordSanitizedError(pending.error, pending.metadata, client);
  }
}

function requestToUrl(input: RequestInfo | URL) {
  if (input instanceof Request) return input.url;
  return input.toString();
}

function requestMethod(input: RequestInfo | URL, init?: RequestInit) {
  return (init?.method ?? (input instanceof Request ? input.method : "GET"))
    .toUpperCase()
    .slice(0, 16);
}

function requestSignal(input: RequestInfo | URL, init?: RequestInit) {
  return init?.signal ?? (input instanceof Request ? input.signal : undefined);
}

function isCodexEventStreamUrl(url: string, origin: string) {
  try {
    const expectedOrigin = new URL(origin).origin;
    const parsed = new URL(url, expectedOrigin);
    return (
      parsed.origin === expectedOrigin &&
      /^\/api\/codex\/conversations\/[^/]+\/events$/.test(parsed.pathname)
    );
  } catch {
    return false;
  }
}

export function installApiCorrelationCapture(
  client: Pick<RumClient, "recordEvent">,
) {
  const originalFetch = window.fetch;
  const origin = window.location.origin;

  const observedFetch: typeof window.fetch = async (
    input: RequestInfo | URL,
    init?: RequestInit,
  ) => {
    const url = requestToUrl(input);
    const startedAt = performance.now();
    let response: Response;
    try {
      response = await originalFetch.call(window, input, init);
    } catch (error) {
      try {
        const wasAborted =
          requestSignal(input, init)?.aborted ||
          getSafeErrorName(error) === "AbortError";
        if (!wasAborted && isSafeApiUrl(url, origin)) {
          const apiFamily = toSafeRumApiFamily(url, origin);
          const durationMs = Math.max(
            0,
            Math.round(performance.now() - startedAt),
          );
          const errorType = getSafeErrorName(error);
          client.recordEvent(
            "sozo.api_correlation",
            {
              version: "1.0.0",
              api_family: apiFamily,
              duration_ms: durationMs,
              error_type: errorType,
              reason: "network_error",
            },
            {
              api_family: apiFamily,
              correlation_reason: "network_error",
              error_type: errorType,
            },
          );
        }
      } catch {
        // Telemetry must never replace the original network failure.
      }
      throw error;
    }
    try {
      const durationMs = Math.max(0, Math.round(performance.now() - startedAt));
      const requestId = safeCorrelationId(response.headers.get("x-request-id"));
      const isOperationalFailure =
        response.status >= 500 || response.status === 429;
      const isSlow =
        durationMs >= SLOW_API_RESPONSE_MS &&
        !isCodexEventStreamUrl(url, origin);

      if (
        requestId &&
        isSafeApiUrl(url, origin) &&
        (isOperationalFailure || isSlow)
      ) {
        const apiFamily = toSafeRumApiFamily(url, origin);
        const reason = isOperationalFailure ? "http_error" : "slow_response";
        client.recordEvent(
          "sozo.api_correlation",
          {
            version: "1.0.0",
            api_family: apiFamily,
            duration_ms: durationMs,
            method: requestMethod(input, init),
            reason,
            request_id: requestId,
            status: response.status,
          },
          {
            api_family: apiFamily,
            correlation_reason: reason,
            request_id: requestId,
          },
        );
      }
    } catch {
      // Telemetry enrichment must not turn a successful fetch into a failure.
    }

    return response;
  };
  window.fetch = observedFetch;

  return () => {
    if (window.fetch === observedFetch) window.fetch = originalFetch;
  };
}

export function buildCloudWatchRumSdkConfig(
  runtime: CloudWatchRumRuntimeConfig,
  origin: string,
): AwsRumConfig {
  const safeApiUrlPatterns = buildSafeApiUrlPatterns(origin);
  const sensitiveApiUrlPatterns = buildSensitiveApiUrlPatterns(origin);
  return {
    allowCookies: false,
    disableAutoPageView: true,
    enableW3CTraceId: true,
    enableXRay: true,
    // Omitting guestRoleArn makes aws-rum-web use Cognito's enhanced flow.
    // Passing both values selects the legacy basic flow, which fails when the
    // identity pool correctly keeps AllowClassicFlow disabled.
    identityPoolId: runtime.identityPoolId,
    // aws-rum-web currently merges its us-west-2 default endpoint before the
    // regional client is created. Pin the dataplane to the app monitor region.
    endpoint: `https://dataplane.rum.${runtime.region}.amazonaws.com`,
    recordResourceUrl: false,
    sessionSampleRate: 1,
    signing: true,
    telemetries: [
      ["errors", { ignore: captureGlobalErrorContext }],
      [
        "http",
        {
          addXRayTraceIdHeader: safeApiUrlPatterns,
          recordAllRequests: true,
          urlsToExclude: sensitiveApiUrlPatterns,
          urlsToInclude: safeApiUrlPatterns,
        },
      ],
    ],
    ...(runtime.releaseId ? { releaseId: runtime.releaseId } : {}),
  };
}

async function createCloudWatchRumClient() {
  if (typeof window === "undefined") return undefined;
  const runtime = resolveCloudWatchRumRuntimeConfig();
  if (!runtime) return undefined;

  const { AwsRum, NavigationPlugin, ResourcePlugin } = await import(
    "aws-rum-web"
  );
  const config = {
    ...buildCloudWatchRumSdkConfig(runtime, window.location.origin),
    // Preserve URL-free navigation/resource timings while replacing the stock
    // attribution-bearing WebVitalsPlugin with safe value-only callbacks.
    eventPluginsToLoad: [new NavigationPlugin(), new ResourcePlugin()],
  };

  const client = new AwsRum(
    runtime.applicationId,
    runtime.releaseId ?? "unversioned",
    runtime.region,
    config,
  );
  rumClient = client;
  setRumMetadataHook(client);
  installApiCorrelationCapture(client);
  void installSafeWebVitalsCapture(client).catch(() => undefined);
  if (currentActor) startRumSession(client);
  recordCurrentPage(client, window.location.pathname);
  try {
    client.recordEvent("sozo.rum_initialized", {
      schema_version: 1,
      release_id: runtime.releaseId ?? "unversioned",
    });
  } catch {
    // A heartbeat failure must not disable the rest of RUM collection.
  }
  recordPendingErrors(client);
  return client;
}

export function initializeCloudWatchRum() {
  initialization ??= createCloudWatchRumClient().catch(() => undefined);
  return initialization;
}

export function setCloudWatchRumPage(pathname: string) {
  if (!rumClient) return;
  recordCurrentPage(rumClient, pathname);
}

export function setCloudWatchRumActor(actor: CloudWatchRumActor) {
  const userCorrelationId = safeObservabilityCorrelationId(
    "user",
    actor.userCorrelationId,
  );
  const role = safeMetadataValue(actor.role);
  if (!userCorrelationId || !role) {
    clearCloudWatchRumActor();
    return;
  }

  const organizationCorrelationId = safeObservabilityCorrelationId(
    "organization",
    actor.organizationCorrelationId,
  );
  const contractType = safeMetadataValue(actor.contractType);
  const nextActor: CloudWatchRumActor = {
    userCorrelationId,
    role,
    ...(organizationCorrelationId ? { organizationCorrelationId } : {}),
    ...(contractType ? { contractType } : {}),
  };
  const userChanged =
    currentActor?.userCorrelationId !== nextActor.userCorrelationId;
  currentActor = nextActor;
  if (rumClient && userChanged) startRumSession(rumClient);
}

export function clearCloudWatchRumActor() {
  if (!currentActor) return;
  currentActor = undefined;
  if (rumClient) startRumSession(rumClient);
}

export function recordCloudWatchRumError(
  error: unknown,
  context: {
    digest?: string;
    errorId?: string;
    handled?: boolean;
    source: CloudWatchRumErrorSource;
  },
) {
  captureCloudWatchRumError(error, {
    ...context,
    handled: context.handled ?? true,
  });
}

function captureCloudWatchRumError(
  error: unknown,
  context: {
    digest?: string;
    errorId?: string;
    handled: boolean;
    source: CloudWatchRumErrorSource;
  },
) {
  const resolvedErrorId = resolveClientErrorId(error, context.errorId);
  const requestId = getRequestId(error);
  const metadata: EventMetadata = {
    error_handled: context.handled,
    error_id: resolvedErrorId,
    error_source: context.source,
    ...(requestId ? { request_id: requestId } : {}),
  };

  recordSanitizedError(error, metadata);

  const errorName = getSafeErrorName(error);
  const systemActionableSource =
    context.source === "app_error_boundary" ||
    context.source === "extension_shell";
  const automaticSource =
    context.source === "window_error" ||
    context.source === "unhandled_rejection";
  const shouldReport =
    errorName === "AbortError"
      ? systemActionableSource
      : errorName === "ChunkLoadError"
        ? systemActionableSource || automaticSource
        : true;
  if (shouldReport) {
    reportCloudWatchClientError(error, {
      ...context,
      errorId: resolvedErrorId,
    });
  }
}

function reportCloudWatchClientError(
  error: unknown,
  context: {
    source: CloudWatchRumErrorSource;
    errorId: string;
    digest?: string;
    handled: boolean;
  },
) {
  if (typeof window === "undefined") return;
  const errorName = getSafeErrorName(error);
  const reportContext =
    errorName === "ChunkLoadError"
      ? "chunk_load_error"
      : context.source === "app_error_boundary" ||
          context.source === "extension_shell"
        ? context.source
        : "rum_error_context";
  if (error !== null && typeof error === "object") {
    const reportedContexts = reportedClientErrorContexts.get(error);
    if (reportedContexts?.has(reportContext)) return;
    if (reportedContexts) reportedContexts.add(reportContext);
    else reportedClientErrorContexts.set(error, new Set([reportContext]));
  }

  const errorId = safeCorrelationId(context.errorId);
  if (!errorId) return;
  const digest = safeCorrelationId(context.digest);
  const requestId = getRequestId(error);
  const browserRelease = nonEmpty(PUBLIC_RUM_ENV.releaseId);
  const payload = {
    source: context.source,
    errorId,
    errorName,
    handled: context.handled,
    ...(digest ? { digest } : {}),
    ...(requestId ? { requestId } : {}),
    ...(isSafeObservabilityRelease(browserRelease) ? { browserRelease } : {}),
    route: toSafeRumPageId(window.location.pathname),
  };

  try {
    void window
      .fetch("/api/client-errors", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        keepalive: true,
      })
      .catch(() => undefined);
  } catch {
    // Error reporting must never change application behavior.
  }
}
